import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CompanyMembership {
  id: string;
  name: string | null;
  /** Per-company role: 'admin' or 'user'. */
  role: string | null;
}

/**
 * Locally cached, company-specific data (e.g. the "recently accessed
 * appliances" list) must not bleed across tenants. Whenever the active
 * company changes we drop those caches.
 */
const LAST_COMPANY_KEY = "activeCompanyId";
const COMPANY_SCOPED_CACHE_KEYS = ["recentlyAccessedAppliances"];

function purgeCachesOnCompanyChange(companyId: string | null) {
  if (!companyId) return;
  try {
    const previous = localStorage.getItem(LAST_COMPANY_KEY);
    if (previous === companyId) return;
    localStorage.setItem(LAST_COMPANY_KEY, companyId);
    if (previous) COMPANY_SCOPED_CACHE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

interface CurrentCompanyState {
  id: string | null;
  name: string | null;
  loading: boolean;
  memberships: CompanyMembership[];
  /** True when the user belongs to >1 company and has not chosen one yet. */
  needsSelection: boolean;
  setActive: (companyId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Returns the current user's active company plus the list of companies they
 * can switch between. For multi-company users the caller should render a
 * picker until `needsSelection` is false.
 */
export function useCurrentCompany(): CurrentCompanyState {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<CompanyMembership[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setActiveId(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    const [membershipsRes, activeRes] = await Promise.all([
      supabase
        .from("user_companies")
        .select("company_id, role, companies:company_id(name)")
        .eq("user_id", user.id),
      supabase
        .from("user_active_company")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle(),
    ]);

    const list: CompanyMembership[] = (membershipsRes.data ?? []).map((row) => {
      const rel = (row as { companies?: { name?: string } | { name?: string }[] }).companies;
      const name = Array.isArray(rel) ? rel[0]?.name ?? null : rel?.name ?? null;
      return { id: row.company_id as string, name, role: (row as { role?: string }).role ?? null };
    });
    setMemberships(list);

    let active = activeRes.data?.company_id ?? null;
    // Auto-resolve when only one membership exists (covers fresh signups
    // where backfill hasn't run yet).
    if (!active && list.length === 1) {
      active = list[0].id;
    }
    purgeCachesOnCompanyChange(active);
    setActiveId(active);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    load().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const setActive = useCallback(
    async (companyId: string) => {
      const { error } = await supabase.rpc("set_active_company", { _company_id: companyId });
      if (error) throw error;
      purgeCachesOnCompanyChange(companyId);
      setActiveId(companyId);
    },
    [],
  );

  const activeName = memberships.find((m) => m.id === activeId)?.name ?? null;
  const needsSelection = !loading && !activeId && memberships.length > 1;

  return {
    id: activeId,
    name: activeName,
    loading,
    memberships,
    needsSelection,
    setActive,
    refresh: load,
  };
}

/**
 * Imperative one-shot fetch of the signed-in user's active company id.
 * Useful inside event handlers / non-hook contexts (e.g. createPassport).
 */
export async function fetchCurrentCompanyId(userId?: string): Promise<string | null> {
  let uid = userId;
  if (!uid) {
    const { data } = await supabase.auth.getSession();
    uid = data?.session?.user?.id;
  }
  if (!uid) return null;

  const { data: active } = await supabase
    .from("user_active_company")
    .select("company_id")
    .eq("user_id", uid)
    .maybeSingle();
  if (active?.company_id) return active.company_id;

  // Fallback: single-company user without an active row yet.
  const { data: memberships } = await supabase
    .from("user_companies")
    .select("company_id")
    .eq("user_id", uid);
  if (memberships && memberships.length === 1) return memberships[0].company_id;
  return null;
}
